//TODO: share with backend

import Stripe from 'stripe';
import { StripePrice } from './stripePrice';

type Status = 'DRAFT' | 'TEST' | 'ACTIVE';

export interface PricingPage {
    id: string;
    name: string;
    plans: Plan[];
    settings: PageSettings;
    designSettings: PageDesignSettings;
    status: Status;
    createdAt?: string;
    secureMode: boolean;
    customerPortalId?: string;
    features?: string[];
}

interface AbstractPaymentFlow {
    id: string;
    paymentSuccessUrl: string | undefined;
    paymentFailureUrl: string | undefined;
    returnToOriginSite: boolean;
}
interface ChargeNowFlow extends AbstractPaymentFlow {
    type: PaymentFlowType.CHARGE_NOW;
}

interface ChargeLaterFlow extends AbstractPaymentFlow {
    type: PaymentFlowType.CHARGE_LATER;
    actionUrl: string | undefined;
}

// Used for custom products that don't relate to a specific Stripe Product
export interface CustomProduct {
    id: string;
    name: string;
    callToActionLabel?: string;
    callToActionUrl?: string;
    price: { id: string; customLabel?: string };
    paypalPlanId?: string;
}

export type Product = StripePrice | CustomProduct;

export type PaymentFlow = ChargeNowFlow | ChargeLaterFlow;

export enum PaymentFlowType {
    CHARGE_NOW = 'CHARGE_NOW',
    CHARGE_LATER = 'CHARGE_LATER',
}

export interface SubscriptionSettings {
    expireAfterPeriods: number | null;
    trialDays: number | null;
    trialSettings?: {
        skipPayment: boolean;
        oncePerCustomer: boolean;
        allowedStripePrices?: string[];
    };
    automaticTax: boolean;
    coupons: boolean;
    adjustableQuantity: boolean;
    skipPayment: {
        enabled: boolean;
        maxPrice?: number;
        avoidDuplicates: boolean;
    };
    resetBillingCycle: {
        enabled: boolean;
        dayOfMonth: number;
    };
    enableAddons: boolean;
    chargeLater: boolean;
    embeddedUi: boolean;
    sendInvoice: boolean;
    collectTaxId: boolean;
}

export const PAYMENT_METHOD_OPTIONS = [
    'card',
    'acss_debit',
    'bacs_debit',
    'sepa_debit',
    'ideal',
    'klarna',
    'afterpay_clearpay',
    'bancontact',
    'alipay',
];

export type PaymentMethod = (typeof PAYMENT_METHOD_OPTIONS)[number];
export interface PaymentSettings {
    requireBillingAddress: boolean;
    requireShippingAddress: boolean;
    paymentMethod: PaymentMethod[];
}

export interface Plan {
    id: string;
    order: number | null;
    name: string;
    description: string;
    headlinePrice?: number | null;
    headlinePriceMonthly?: boolean;
    overridePrice?: number | null;
    stripeProductId: string | null;
    stripePriceId: string | null;
    customProduct: CustomProduct | null;
    product: Product;
    productType: 'recurring' | 'one-off';
    features: Feature[];
    designSettings: PlanDesignSettings;
    paymentFlow: PaymentFlow;
    paymentSettings: PaymentSettings;
    subscriptionSettings: SubscriptionSettings;
    currentUserSubscribed?: boolean;
    currentUserSubscription?: Stripe.Subscription | null;
    callToActionUrl?: string;
    paypalPlanId?: string;
    addons?: PlanAddon[];
}

export interface PlanAddon {
    stripePriceId: string;
    description: string;
    product?: StripePrice;
    adjustableQuantity: boolean;
    maxQuantity?: string;
}

export interface PlanDesignSettings {
    id: string;
    buttonText: string;
}

export interface Feature {
    id: string;
    name: string;
    allPlans: boolean;
    visible: boolean;
    slug: string;
    subdued: boolean;
    order?: number;
}

interface PageCustomWording {
    weekAbbreviation?: string;
    monthAbbreviation?: string;
    monthsAbbreviation?: string;
    yearAbbreviation?: string;
    weeklyAbbreviation?: string;
    monthlyAbbreviation?: string;
    quarterlyAbbreviation?: string;
    yearlyAbbreviation?: string;
    users?: string;
    upToXUsers?: string;
}

export enum FrequencyOrder {
    LOW_HIGH = 'LOW_HIGH',
    HIGH_LOW = 'HIGH_LOW',
}
export interface PageSettings {
    id: string;
    monthlyYearlyToggle: boolean;
    displayMonthlyPrice: boolean;
    promotedPlan: boolean;
    promotedFrequency: boolean;
    promotedFrequencyWording: string;
    defaultFrequency?: string;
    frequencyOrder?: FrequencyOrder;
    customWording: PageCustomWording;
    planPicker: PlanPickerSettings;
}

interface PlanPickerSettings {
    enabled: boolean;
    proration: boolean;
    disableOneOff: boolean;
    disableZeroPrice: boolean;
    confirmChanges: boolean;
    scheduleUpgrades: boolean;
    scheduleDowngrades: boolean;
}

export enum FeatureIcon {
    CIRCLE = 'circle',
    SQUARE = 'square',
    BOLD = 'bold',
    GREEN = 'green',
    PURPLE = 'purple',
}

export enum SubduedIcon {
    RED_X = 'red-x',
    GREY_X = 'grey-x',
}

export type CustomIcon = string;

export enum BorderRadius {
    SMALL = 'small',
    MEDIUM = 'medium',
    LARGE = 'large',
    EXTRALARGE = 'extra-large',
}

export enum Shadow {
    SMALL = 'small',
    MEDIUM = 'medium',
    LARGE = 'large',
}

export interface PageDesignSettings {
    id: string;
    backgroundColor: string;
    font?: string;
    buttonGradient: boolean;
    borderRadius: 'none' | BorderRadius;
    featureIcon: 'none' | FeatureIcon | CustomIcon;
    subduedIcon: 'none' | SubduedIcon;
    shadow: 'none' | Shadow;
    animatePlanOnHover: boolean;
    disableFlexCol: boolean;
    customCSS: string;
    companyName: string;
    mostPopular: {
        enabled: boolean;
        wording: string;
        stripeProductId?: string;
    };
    layout: {
        button: 'top' | 'bottom';
    };
    hideProductImages: boolean;
}

export enum StripeMode {
    LIVE = 'live',
    TEST = 'test',
}
